// Material Requests — Dispatch raises, Admin / Super Admin approve, the store
// issues (client, 2026-10-24). Server: src/api-inventory/material-requests.js.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, ClipboardList, Layers, PackageCheck, Plus, Search, Trash2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { cn, localToday } from '@/shared/utils/utils';
import {
  useMaterialRequests, useMaterialRequestAction, materialErrorText,
  type MaterialRequest, type RequestLine, type RequestStatus,
} from '../hooks/useMaterials';
import { expandBom } from '../hooks/useStockControl';
import { FieldLabel, FilterChips, ItemNameInput, Panel, Pill, fmtDateTime, fmtDay, fmtQty, type PillTone } from './materialShared';

const STATUS_TONE: Record<RequestStatus, PillTone> = {
  pending_approval: 'warning', approved: 'info', partially_issued: 'primary', issued: 'success', rejected: 'danger', cancelled: 'neutral',
};
const PRIORITY_TONE = { normal: 'neutral', high: 'warning', urgent: 'danger' } as const;
const UNITS = ['NOS', 'KG', 'LTR', 'ML', 'GM', 'BOX', 'DRUM', 'BKT'];

type Filter = 'open' | 'pending_approval' | 'approved' | 'issued' | 'rejected' | 'all';

const remaining = (l: RequestLine) => Math.max(0, (l.approvedQty ?? l.quantity) - (l.issuedQty || 0));

export function MaterialRequestsTab({ openNew = 0 }: { openNew?: number }) {
  const [filter, setFilter] = useState<Filter>('open');
  const [q, setQ] = useState('');
  const [mine, setMine] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading } = useMaterialRequests(filter, q, mine);
  useEffect(() => { if (openNew) setCreating(true); }, [openNew]);
  // Opened from the dashboard's "Waiting for your approval": ?request=<id>
  const [searchParams] = useSearchParams();
  const linked = searchParams.get('request');
  useEffect(() => { if (linked) setOpenId(linked); }, [linked]);

  const counts = data?.counts || {};
  const open = (counts.pending_approval || 0) + (counts.approved || 0) + (counts.partially_issued || 0);
  const selected = data?.requests.find((r) => r._id === openId) || null;

  return (
    <div className="space-y-4">
      <Panel
        title="Material requests"
        subtitle={data?.canApprove ? 'Requests from Dispatch and other teams. You approve; the store issues.' : 'Ask for the materials a job needs. Admin approves, then the store issues them.'}
        actions={<Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> Raise request</Button>}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <FilterChips<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'open', label: 'Open', count: open },
              { value: 'pending_approval', label: 'Awaiting approval', count: counts.pending_approval || 0 },
              { value: 'approved', label: 'To issue', count: (counts.approved || 0) + (counts.partially_issued || 0) },
              { value: 'issued', label: 'Issued', count: counts.issued || 0 },
              { value: 'rejected', label: 'Rejected', count: counts.rejected || 0 },
              { value: 'all', label: 'All' },
            ]}
          />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-body-sm text-on-surface-variant">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="h-4 w-4 accent-primary" /> Only mine
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" aria-hidden />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Number, item, order…" className="h-9 w-56 rounded-lg border border-input bg-surface pl-8 pr-3 text-body-sm outline-none focus:border-primary" />
            </div>
          </div>
        </div>

        {isLoading ? <div className="p-5"><Skeleton className="h-40" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-body-sm">
              <thead className="border-y border-border bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
                <tr>
                  <th className="px-5 py-2.5">Request</th>
                  <th className="px-3 py-2.5">Items</th>
                  <th className="px-3 py-2.5">For</th>
                  <th className="px-3 py-2.5">Raised by</th>
                  <th className="px-3 py-2.5">Needed by</th>
                  <th className="px-5 py-2.5 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.requests || []).map((r) => (
                  <tr key={r._id} className="cursor-pointer hover:bg-surface-container-low/60" onClick={() => setOpenId(r._id)}>
                    <td className="whitespace-nowrap px-5 py-3">
                      <div className="font-mono font-semibold text-on-surface">{r.requestNumber}</div>
                      <div className="text-data-xs text-on-surface-subtle">{fmtDateTime(r.createdAt)}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="max-w-[300px] truncate text-on-surface" title={r.lines.map((l) => `${l.itemName} × ${l.quantity}`).join(', ')}>
                        {r.lines.slice(0, 2).map((l) => `${l.itemName} × ${fmtQty(l.quantity)} ${l.unit}`).join(', ')}
                        {r.lines.length > 2 && ` +${r.lines.length - 2} more`}
                      </div>
                      {r.lines.some((l) => l.onHand !== null && l.onHand < remaining(l)) && ['pending_approval', 'approved', 'partially_issued'].includes(r.status) && (
                        <div className="text-data-xs font-medium text-warning-text">Some items short in stock</div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-on-surface-variant">
                      {data?.purposes?.[r.purpose] || r.purpose}
                      {r.reference && <div className="text-data-xs text-on-surface-subtle">{r.reference}</div>}
                    </td>
                    <td className="px-3 py-3 text-on-surface-variant">{r.requestedByName}</td>
                    <td className="px-3 py-3">
                      <span className="text-on-surface-variant">{fmtDay(r.requiredBy)}</span>
                      {r.priority !== 'normal' && <Pill tone={PRIORITY_TONE[r.priority]} className="ml-2">{r.priority}</Pill>}
                    </td>
                    <td className="px-5 py-3 text-right"><Pill tone={STATUS_TONE[r.status]}>{data?.statuses?.[r.status] || r.status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!data?.requests.length && (
              <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
                <ClipboardList className="h-8 w-8 text-on-surface-subtle" aria-hidden />
                <p className="text-body-md font-medium text-on-surface">No requests here</p>
                <p className="text-body-sm text-on-surface-variant">Raise one for drums, caps, labels, base oil or anything else a job needs.</p>
              </div>
            )}
          </div>
        )}
      </Panel>

      <NewRequestModal open={creating} onClose={() => setCreating(false)} purposes={data?.purposes || {}} categories={data?.categories || {}} onCreated={(id) => { setCreating(false); setOpenId(id); }} />
      {selected && data && (
        <RequestDetailModal request={selected} canApprove={data.canApprove} me={data.me} purposes={data.purposes} statuses={data.statuses} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

type DraftLine = { key: number; productId: string | null; itemName: string; quantity: string; unit: string; category: string; source: 'warehouse' | 'tank'; tankCode: string; onHand: number | null };
const blankLine = (key: number): DraftLine => ({ key, productId: null, itemName: '', quantity: '', unit: 'NOS', category: 'packaging_material', source: 'warehouse', tankCode: '', onHand: null });

function NewRequestModal({ open, onClose, purposes, categories, onCreated }: { open: boolean; onClose: () => void; purposes: Record<string, string>; categories: Record<string, string>; onCreated: (id: string) => void }) {
  const action = useMaterialRequestAction();
  const [purpose, setPurpose] = useState('dispatch');
  const [reference, setReference] = useState('');
  const [requiredBy, setRequiredBy] = useState(localToday());
  const [priority, setPriority] = useState<'normal' | 'high' | 'urgent'>('normal');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([blankLine(1)]);
  useEffect(() => {
    if (!open) return;
    setPurpose('dispatch'); setReference(''); setRequiredBy(localToday()); setPriority('normal'); setNotes(''); setLines([blankLine(1)]);
  }, [open]);
  const patch = (key: number, p: Partial<DraftLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)));
  const filled = lines.filter((l) => l.itemName.trim() && Number(l.quantity) > 0);
  const [bomItem, setBomItem] = useState<{ itemName: string; productId: string | null }>({ itemName: '', productId: null });
  const [bomUnits, setBomUnits] = useState('');
  const [bomBusy, setBomBusy] = useState(false);
  const fillFromBom = async () => {
    if (!bomItem.itemName.trim() || !(Number(bomUnits) > 0)) return;
    setBomBusy(true);
    try {
      const out = await expandBom(bomItem, Number(bomUnits));
      setLines(out.map((l, i) => ({
        key: i + 1, productId: l.productId, itemName: l.itemName, quantity: String(l.quantity), unit: l.unit,
        category: l.category, source: l.source, tankCode: '', onHand: l.onHand,
      })));
      if (!reference) setReference(`${bomUnits} × ${bomItem.itemName}`);
      toast.success(`Filled ${out.length} items for ${bomUnits} × ${bomItem.itemName}`);
    } catch (err) {
      toast.error(materialErrorText(err, 'Could not fill from the BOM.'));
    } finally { setBomBusy(false); }
  };

  const submit = async () => {
    try {
      const res = await action.mutateAsync({
        action: 'create',
        body: {
          purpose, reference, requiredBy, priority, notes,
          lines: lines.map((l) => ({ productId: l.productId, itemName: l.itemName, quantity: Number(l.quantity), unit: l.unit, category: l.category, source: l.source, tankCode: l.tankCode })),
        },
      });
      toast.success(`${res.request.requestNumber} sent for approval`);
      if (res.itemsAdded?.length) toast(`New item${res.itemsAdded.length === 1 ? '' : 's'} added to the list: ${res.itemsAdded.join(', ')}`, { icon: '➕', duration: 6000 });
      onCreated(res.request._id);
    } catch (err) {
      toast.error(materialErrorText(err, 'Could not raise the request.'));
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="2xl" title="Raise material request" footer={(
      <>
        <span className="mr-auto text-body-sm text-on-surface-variant">{filled.length} item{filled.length === 1 ? '' : 's'} · goes to Admin for approval</span>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={action.isPending} disabled={!filled.length}>Send for approval</Button>
      </>
    )}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <label className="block"><FieldLabel>Needed for</FieldLabel>
            <Select value={purpose} onChange={(e) => setPurpose(e.target.value)} options={Object.entries(purposes).map(([value, label]) => ({ value, label }))} />
          </label>
          <Input label="Order / customer / job" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. SO/2026-27/0300" />
          <Input label="Needed by" type="date" value={requiredBy} min={localToday()} onChange={(e) => setRequiredBy(e.target.value)} />
          <label className="block"><FieldLabel>Priority</FieldLabel>
            <Select value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)} options={[{ value: 'normal', label: 'Normal' }, { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' }]} />
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-primary/20 bg-primary-container/30 p-3">
          <Layers className="mb-2.5 h-4 w-4 text-primary-dim" aria-hidden />
          <div className="min-w-[260px] flex-1">
            <FieldLabel>Fill from BOM — finished item</FieldLabel>
            <ItemNameInput value={bomItem.itemName} onChange={(n) => setBomItem({ itemName: n.itemName, productId: n.productId })} placeholder="e.g. RBM HYDRAULIC OIL AW 68 210 LTR" />
          </div>
          <div className="w-28"><Input label="Units" type="number" min="1" step="1" value={bomUnits} onChange={(e) => setBomUnits(e.target.value)} placeholder="20" /></div>
          <Button variant="outline" onClick={fillFromBom} loading={bomBusy} disabled={!bomItem.itemName.trim() || !(Number(bomUnits) > 0)}>Fill items</Button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[820px] text-body-sm">
            <thead className="bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
              <tr>
                <th className="w-8 px-3 py-2">#</th>
                <th className="px-2 py-2">Item</th>
                <th className="w-28 px-2 py-2">Quantity</th>
                <th className="w-24 px-2 py-2">Unit</th>
                <th className="w-44 px-2 py-2">Type</th>
                <th className="w-40 px-2 py-2">From</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {lines.map((l, i) => (
                <tr key={l.key} className="align-top">
                  <td className="px-3 py-2.5 text-on-surface-subtle">{i + 1}</td>
                  <td className="px-2 py-2">
                    <ItemNameInput value={l.itemName} onChange={(n) => patch(l.key, { itemName: n.itemName, productId: n.productId, onHand: null })} placeholder="e.g. MP3 Grease, SN 70 base oil, 210L drum" />
                    {l.productId && l.onHand !== null && (
                      <div className={cn('mt-1 text-data-xs', l.onHand >= Number(l.quantity || 0) ? 'text-success-text' : 'text-warning-text')}>
                        {fmtQty(l.onHand)} in stock{l.onHand < Number(l.quantity || 0) ? ' — short' : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2"><input type="number" min="0" step="any" value={l.quantity} onChange={(e) => patch(l.key, { quantity: e.target.value })} className="h-9 w-full rounded-lg border border-input bg-surface px-2 text-right font-mono text-body-sm outline-none focus:border-primary" /></td>
                  <td className="px-2 py-2"><Select value={l.unit} onChange={(e) => patch(l.key, { unit: e.target.value })} options={UNITS.map((u) => ({ value: u, label: u }))} /></td>
                  <td className="px-2 py-2"><Select value={l.category} onChange={(e) => patch(l.key, { category: e.target.value })} options={Object.entries(categories).map(([value, label]) => ({ value, label }))} /></td>
                  <td className="px-2 py-2">
                    <Select value={l.source} onChange={(e) => patch(l.key, { source: e.target.value as DraftLine['source'] })} options={[{ value: 'warehouse', label: 'Store / warehouse' }, { value: 'tank', label: 'Tank (bulk oil)' }]} />
                    {l.source === 'tank' && <input value={l.tankCode} onChange={(e) => patch(l.key, { tankCode: e.target.value.toUpperCase() })} placeholder="Tank e.g. T5" className="mt-1 h-8 w-full rounded-lg border border-input bg-surface px-2 text-body-sm outline-none focus:border-primary" />}
                  </td>
                  <td className="px-1 py-2">
                    {lines.length > 1 && <button type="button" aria-label="Remove line" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} className="rounded p-1.5 text-on-surface-subtle hover:bg-danger/10 hover:text-danger-text"><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-border px-3 py-2">
            <Button variant="ghost" size="sm" onClick={() => setLines((ls) => [...ls, blankLine(Math.max(...ls.map((x) => x.key)) + 1)])}><Plus className="h-4 w-4" /> Add item</Button>
          </div>
        </div>
        <label className="block"><FieldLabel>Note for the approver</FieldLabel>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-body-sm outline-none focus:border-primary" placeholder="Why it is needed, anything urgent…" />
        </label>
      </div>
    </Modal>
  );
}

function RequestDetailModal({ request: r, canApprove, me, purposes, statuses, onClose }: {
  request: MaterialRequest; canApprove: boolean; me: string; purposes: Record<string, string>; statuses: Record<RequestStatus, string>; onClose: () => void;
}) {
  const action = useMaterialRequestAction();
  const mineReq = r.requestedBy === me;
  const [qty, setQty] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<'view' | 'approve' | 'issue' | 'reject'>('view');
  const [note, setNote] = useState('');
  useEffect(() => { setMode('view'); setNote(''); setQty({}); }, [r._id]);

  const startApprove = () => { setQty(Object.fromEntries(r.lines.map((l) => [l._id, String(l.quantity)]))); setMode('approve'); };
  const startIssue = () => { setQty(Object.fromEntries(r.lines.map((l) => [l._id, String(remaining(l))]))); setMode('issue'); };

  const run = async (kind: 'approve' | 'reject' | 'issue' | 'cancel') => {
    try {
      const body = kind === 'approve' ? { note, lines: r.lines.map((l) => ({ lineId: l._id, approvedQty: Number(qty[l._id] ?? l.quantity) })) }
        : kind === 'issue' ? { lines: r.lines.map((l) => ({ lineId: l._id, issueQty: Number(qty[l._id] ?? 0) })) }
          : { reason: note };
      const res = await action.mutateAsync({ id: r._id, action: kind, body });
      toast.success({ approve: 'Approved', reject: 'Rejected', issue: 'Materials issued', cancel: 'Request cancelled' }[kind]);
      (res.warnings || []).forEach((w) => toast(w, { icon: '⚠️', duration: 7000 }));
      setMode('view');
    } catch (err) {
      toast.error(materialErrorText(err, 'That did not work.'));
    }
  };

  const canDecide = canApprove && !mineReq && r.status === 'pending_approval';
  const canIssue = ['approved', 'partially_issued'].includes(r.status);
  const canCancel = (mineReq || canApprove) && ['pending_approval', 'approved'].includes(r.status);
  const editing = mode === 'approve' || mode === 'issue';

  return (
    <Modal open onClose={onClose} size="xl" title={(
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-mono">{r.requestNumber}</span>
        <Pill tone={STATUS_TONE[r.status]}>{statuses[r.status]}</Pill>
        {r.priority !== 'normal' && <Pill tone={PRIORITY_TONE[r.priority]}>{r.priority}</Pill>}
      </span>
    )} footer={(
      <>
        {mode === 'view' && (
          <>
            {canCancel && <Button variant="ghost" className="mr-auto" onClick={() => { setNote(''); run('cancel'); }} loading={action.isPending}>Cancel request</Button>}
            {canDecide && <Button variant="outline" onClick={() => setMode('reject')}><XCircle className="h-4 w-4" /> Reject</Button>}
            {canDecide && <Button onClick={startApprove}><CheckCircle2 className="h-4 w-4" /> Approve</Button>}
            {canIssue && <Button onClick={startIssue}><PackageCheck className="h-4 w-4" /> Issue materials</Button>}
            {!canDecide && !canIssue && !canCancel && <Button variant="outline" onClick={onClose}>Close</Button>}
          </>
        )}
        {mode === 'approve' && <><Button variant="outline" onClick={() => setMode('view')}>Back</Button><Button onClick={() => run('approve')} loading={action.isPending}>Confirm approval</Button></>}
        {mode === 'issue' && <><Button variant="outline" onClick={() => setMode('view')}>Back</Button><Button onClick={() => run('issue')} loading={action.isPending}>Confirm issue</Button></>}
        {mode === 'reject' && <><Button variant="outline" onClick={() => setMode('view')}>Back</Button><Button variant="danger" onClick={() => run('reject')} loading={action.isPending} disabled={!note.trim()}>Reject request</Button></>}
      </>
    )}>
      <div className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-body-sm sm:grid-cols-4">
          <div><dt className="text-on-surface-variant">Needed for</dt><dd className="font-medium text-on-surface">{purposes[r.purpose] || r.purpose}</dd></div>
          <div><dt className="text-on-surface-variant">Reference</dt><dd className="font-medium text-on-surface">{r.reference || '—'}</dd></div>
          <div><dt className="text-on-surface-variant">Needed by</dt><dd className="font-medium text-on-surface">{fmtDay(r.requiredBy)}</dd></div>
          <div><dt className="text-on-surface-variant">Raised by</dt><dd className="font-medium text-on-surface">{r.requestedByName}{r.requestedByDept ? ` · ${r.requestedByDept}` : ''}</dd></div>
        </dl>
        {mineReq && canApprove && r.status === 'pending_approval' && (
          <p className="rounded-lg border border-info/25 bg-info/10 px-3 py-2 text-body-sm text-info-text">You raised this request, so another Admin or Super Admin has to approve it.</p>
        )}

        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[640px] text-body-sm">
            <thead className="bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
              <tr>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2 text-right">Asked</th>
                <th className="px-3 py-2 text-right">Approved</th>
                <th className="px-3 py-2 text-right">Issued</th>
                <th className="px-3 py-2 text-right">In stock</th>
                {editing && <th className="w-32 px-3 py-2 text-right">{mode === 'approve' ? 'Approve' : 'Issue now'}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {r.lines.map((l) => (
                <tr key={l._id}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-on-surface">{l.itemName}</div>
                    <div className="text-data-xs text-on-surface-subtle">{l.unit}{l.source === 'tank' ? ` · from tank ${l.tankCode || ''}` : ''}{!l.productId ? ' · not in Item Master (no stock update)' : ''}</div>
                    {!!l.issuedLots?.length && <div className="text-data-xs text-on-surface-variant">Batches: {l.issuedLots.map((b) => `${b.lotNumber} × ${fmtQty(b.quantity)}`).join(', ')}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtQty(l.quantity)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtQty(l.approvedQty)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtQty(l.issuedQty)}</td>
                  <td className={cn('px-3 py-2.5 text-right font-mono tabular-nums', l.onHand !== null && l.onHand < remaining(l) ? 'text-warning-text' : 'text-on-surface-variant')}>{l.onHand === null ? '—' : fmtQty(l.onHand)}</td>
                  {editing && (
                    <td className="px-3 py-2">
                      <input type="number" min="0" step="any" max={mode === 'approve' ? l.quantity : remaining(l)} value={qty[l._id] ?? ''} onChange={(e) => setQty({ ...qty, [l._id]: e.target.value })} className="h-8 w-full rounded-lg border border-input bg-surface px-2 text-right font-mono outline-none focus:border-primary" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(mode === 'approve' || mode === 'reject') && (
          <label className="block"><FieldLabel required={mode === 'reject'}>{mode === 'reject' ? 'Reason for rejecting' : 'Note to the requester (optional)'}</FieldLabel>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} autoFocus className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-body-sm outline-none focus:border-primary" />
          </label>
        )}
        {r.notes && <p className="rounded-lg bg-surface-container-low px-3 py-2 text-body-sm text-on-surface"><span className="font-medium">Note: </span>{r.notes}</p>}
        {r.decisionNote && <p className="rounded-lg bg-surface-container-low px-3 py-2 text-body-sm text-on-surface"><span className="font-medium">{r.status === 'rejected' ? 'Rejected' : 'Approver'} ({r.decidedByName}): </span>{r.decisionNote}</p>}

        <div>
          <h4 className="mb-2 text-label-caps uppercase text-on-surface-variant">History</h4>
          <ol className="space-y-1.5 border-l border-border pl-4">
            {r.history.map((h, i) => (
              <li key={i} className="relative text-body-sm">
                <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" aria-hidden />
                <span className="font-medium capitalize text-on-surface">{h.action}</span>
                <span className="text-on-surface-variant"> · {h.byName} · {fmtDateTime(h.at)}</span>
                {h.note && <div className="text-on-surface-variant">{h.note}</div>}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Modal>
  );
}

export default MaterialRequestsTab;
